import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import * as _ from "lodash";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { User } from "../helpers/models/user";
import { ToasterDataType } from "../helpers/models/toaster-data";

type AuthStateType = {
  user: User;
  token: string;
  isLoggedIn: boolean;
  isExternalAuth: boolean;
};

type ToasterDataType = {
  message: string;
  type: "success" | "error";
  callback: (index: number) => void;
};

type GlobalStateType = AuthStateType & {
  language: string;
  direction: string;
  isLoading: boolean;
  toasters: ToasterDataType[];
  setAuthState: (authState: AuthStateType) => void;
  setUser: (user: User) => void;
  loadGlobalState: () => void;
  clearAuthState: () => void;
  setLanguage: (language: string) => void;
  setLoading: (loading: boolean) => void;
  addToaster: (toaster: ToasterDataType) => void;
  removeToaster: (index: number) => void;
};

// Create the context with default values
const GlobalStateContext = createContext<GlobalStateType | undefined>(undefined);

export const GlobalStateProvider = ({ children }: { children: ReactNode }) => {
  const initialUser = {
    id: null,
    name: "No Name",
    phone: "",
    email: "",
    picture: "",
  };

  const initAuthState: AuthStateType = {
    user: initialUser,
    token: "",
    isLoggedIn: false,
    isExternalAuth: false,
  };

  const [isInitialized, setInitialized] = useState(false);
  const [authState, setAuthState] = useState<AuthStateType>(initAuthState);
  const [isLoading, setLoading] = useState(true);
  const [toasters, setToasters] = useState<ToasterDataType[]>([]);
  const [language, setLanguage] = useState<string>("fr");
  const [direction, setDirection] = useState<string>("ltr");
  const { i18n } = useTranslation();
  const router = useRouter();
  const { pathname, asPath, query } = router;

  // Update localStorage whenever the authState changes
  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    localStorage.setItem("user", JSON.stringify(_.omitBy(authState.user, _.isNull)));
    localStorage.setItem("token", authState.token || "");
    localStorage.setItem("isLoggedIn", String(authState.isLoggedIn));
    localStorage.setItem("isExternalAuth", String(authState.isExternalAuth));
  }, [authState, isInitialized]);

  // Update localStorage whenever the language changes
  // Ce useEffect s'exécute une seule fois au montage
  useEffect(() => {
    if (window.location.pathname.includes("/ar")) {
      // On force la langue à "ar" si ce n'est pas déjà le cas
      const storedLang = localStorage.getItem("language");
      if (storedLang !== "ar") {
        localStorage.setItem("language", "ar");
        setLanguage("ar");
      }
    }
  }, []);
  useEffect(() => {
    if (!isInitialized) return;

    localStorage.setItem("language", language);

    const changeLanguage = async (_language) => {
      setDirection(_language === "ar" ? "rtl" : "ltr");
      await Promise.all([
        i18n.changeLanguage(_language),
        router.push({ pathname, query }, asPath, { locale: _language }),
      ]);
    };

    changeLanguage(language).then();
  }, [language, isInitialized]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    document.documentElement.dir = direction;
  }, [direction, isInitialized]);

  const loadGlobalState = () => {
    const user = localStorage.getItem("user");
    const authState = {
      user: user ? JSON.parse(user || "{}") : initialUser,
      token: localStorage.getItem("token") || "",
      isLoggedIn: localStorage.getItem("isLoggedIn") === "true" || false,
      isExternalAuth: localStorage.getItem("isExternalAuth") === "true" || false,
    };
    setAuthState(authState);
    setLanguage(localStorage.getItem("language") || "fr");
    // setDirection(localStorage.getItem('language') === 'ar' ? 'rtl' : 'ltr');
    setInitialized(true);
  };

  const clearAuthState = () => {
    setAuthState({
      user: initialUser,
      token: "",
      isLoggedIn: false,
      isExternalAuth: false,
    });
    // Clear localStorage for auth keys but not for language
    localStorage.clear();
  };

  const addToaster = (toaster: ToasterDataType) => {
    setToasters([...toasters, toaster]);
  };

  const removeToaster = (index: number) => {
    const restOfToasters = toasters.filter((_, i) => i !== index);
    setToasters([...restOfToasters]);
  };

  const setUser = (user: User) => {
    setAuthState({
      ...authState,
      user,
    });
  };

  return (
    <GlobalStateContext.Provider
      value={{
        ...authState,
        language,
        direction,
        isLoading,
        toasters,
        setAuthState,
        setUser,
        loadGlobalState,
        clearAuthState,
        setLanguage,
        setLoading,
        addToaster,
        removeToaster,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

// Custom hook to use the GlobalStateContext
export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error("useGlobalState must be used within a GlobalStateProvider");
  }
  return context;
};
